'use client'

import { useForm, useWatch } from 'react-hook-form'
import { ContactUsFormData } from '@/types/types'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { Button } from '@/General/Button'
import { useStore } from '@/store'
import { useTurnstile } from '@/Turnstile'
import { useSubmitContactFormMutation } from '@/generated/graphql'
import { useEffect, useState } from 'react'
import { EMAIL_REGEX } from '@/validation'
import { tracking } from '@/tracking'
import { useRouter } from 'next/navigation'

export const ContactForm = () => {
  const router = useRouter()
  const { executeTurnstile } = useTurnstile()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const user = useStore(state => state.user)

  const {
    register,
    reset,
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm<ContactUsFormData>({
    defaultValues: {
      name:
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : user?.firstName || user?.lastName || '',
      email: user?.email,
      reason: '',
      question: '',
      orderNumber: '',
    },
  })

  useEffect(() => {
    if (user) {
      reset({
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`.trim()
            : user.firstName || user.lastName || '',
        email: user.email,
      })
    }
  }, [user, reset])

  const { mutateAsync: submitContactForm } = useSubmitContactFormMutation()

  const selectedReason = useWatch({ control, name: 'reason' }) ?? ''

  const handleFormSubmit = async (data: ContactUsFormData) => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const token = await executeTurnstile()
      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setIsSubmitting(false)
        setError('root', {
          type: 'manual',
          message:
            'Unfortunately we were unable to verify that a human was submitting this form. Please try emailing us at magic@vanishingincmagic.com instead.',
        })
        return
      }

      tracking.track('Contacted us', {
        'Contact us reason': data.reason,
      })

      const result = await submitContactForm({
        name: data.name,
        email: data.email,
        reason: data.reason,
        question: data.question,
        orderNumber: data.orderNumber,
      })

      if (result.submitContactForm) {
        const params = new URLSearchParams({
          email: data.email,
        })
        router.push(`/contact-us/thank-you/?${params.toString()}`)
        return
      }

      setIsSubmitting(false)
      setError('root', {
        type: 'manual',
        message:
          'Something went wrong with the submission. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    } catch {
      setIsSubmitting(false)
      setError('root', {
        type: 'manual',
        message:
          'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    }
  }

  return (
    <form
      className="space-y-6 my-4"
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
    >
      <ErrorBox
        errors={
          Object.values(errors)
            .map(error => error.message)
            .filter(Boolean) as string[]
        }
      />

      {/* Name Field */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="block">
            Your name:
          </label>
          <input
            {...register('name', { required: 'Name is required' })}
            type="text"
            id="name"
            className={`w-full p-2 border ${
              errors.name
                ? 'border-red-500 focus:ring-red-300'
                : 'border-gray-300'
            } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200`}
          />
          {errors.name && (
            <p className="text-red-500 text-sm">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <label htmlFor="email" className="block">
            Your email address:
          </label>
          <input
            disabled={!!user?.email}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Invalid email address',
              },
            })}
            type="email"
            id="email"
            className={`w-full p-2 border ${
              errors.email
                ? 'border-red-500 focus:ring-red-300'
                : 'border-gray-300'
            } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200`}
          />
          {errors.email && (
            <p className="text-red-500 text-sm">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Reason Field */}
      <div className="space-y-2">
        <label htmlFor="reason" className="block">
          How can we help:
        </label>
        <select
          {...register('reason', {
            required: 'Please select a reason for your enquiry',
          })}
          id="reason"
          className={`w-full p-2 border ${
            errors.reason
              ? 'border-red-500 focus:ring-red-300'
              : 'border-gray-300'
          } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200`}
        >
          <option value="">Select an option</option>
          <option value="product">Product question</option>
          <option value="tracking">Track my order</option>
          <option value="problem">Problem with my order</option>
          <option value="changeorder">Change my order</option>
          <option value="return">Return a product</option>
          <option value="partnership">
            Business development/partnership request
          </option>
          <option value="submission">Product submission</option>
          <option value="applyingforjob">Applying for a job</option>
          <option value="somethingelse">Something else</option>
        </select>
        {errors.reason && (
          <p className="text-red-500 text-sm">{errors.reason.message}</p>
        )}
      </div>

      {/* Order Number Field (Conditional) */}
      {['tracking', 'problem', 'changeorder'].includes(selectedReason) && (
        <div className="space-y-2">
          <label htmlFor="orderNumber" className="block">
            Your order number:
          </label>
          <input
            {...register('orderNumber', {
              required: 'Order number is required',
            })}
            type="text"
            id="orderNumber"
            className={`w-full p-2 border ${
              errors.orderNumber
                ? 'border-red-500 focus:ring-red-300'
                : 'border-gray-300'
            } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200`}
          />
          {errors.orderNumber && (
            <p className="text-red-500 text-sm">{errors.orderNumber.message}</p>
          )}
        </div>
      )}

      {/* Question Field */}
      <div className="space-y-2">
        <label htmlFor="question" className="block">
          Your question:
        </label>
        <textarea
          {...register('question', { required: 'Please enter your question' })}
          id="question"
          rows={6}
          className={`w-full p-2 border ${
            errors.question
              ? 'border-red-500 focus:ring-red-300'
              : 'border-gray-300'
          } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200`}
        ></textarea>
        {errors.question && (
          <p className="text-red-500 text-sm">{errors.question.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="text-right">
                <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit your question'}
        </Button>
      </div>
    </form>
  )
}
